import { isEmail } from 'validator';
import moment from 'moment';

const ONE_DIGIT_PATTERN = /[0-9]/;
const ONE_LOWERCASE_LETTER_PATTERN = /[a-z]/;
const ONE_UPPERCASE_LETTER_PATTERN = /[A-Z]/;

export type ValidationResult =
  | false
  | {
      message: Message | string;
      details?: object;
    };

export interface Validator {
  (...args: any[]): ValidationResult;
}

interface ValidatorConstructor {
  (...args: any[]): Validator;
}

interface Constraints {
  [field: string]: Validator[];
}

interface ValidationResults {
  [field: string]: ValidationResult;
}

export interface Values {
  [name: string]: any;
}

//@translate
type Message =
  | 'validator.required'
  | 'validator.password'
  | 'validator.email'
  | 'validator.phone'
  | 'validator.minimum'
  | 'validator.maximum'
  | 'validator.inn'
  | 'validator.bic'
  | 'validator.length'
  | 'validator.maxLength'
  | 'validator.cardNumber'
  | 'validator.ismultiple'
  | 'validator.accountNumber';

export const required: ValidatorConstructor = () => (value: any) =>
  !value && value !== '0' && { message: 'validator.required' };

export const regexp: ValidatorConstructor = (regex: RegExp, message: string) => (value: any) =>
  value && !regex.test(value) && { message };

export const minimum: ValidatorConstructor =
  (min: number, message?: string, details?: any) => (value: any) =>
    !isNaN(+value) &&
    value < min && { message: message || 'validator.minimum', details: { min, ...details } };

export const maximum: ValidatorConstructor =
  (max: number, message?: string, details?: any) => (value: any) =>
    +value > max && { message: message || 'validator.maximum', details: { max, ...details } };

export const isMultiple: ValidatorConstructor =
  (factor: number, message?: string, details?: any) => (value: any) =>
    +value % factor !== 0 && {
      message: message || 'validator.ismultiple',
      details: { factor, ...details },
    };

export const password: ValidatorConstructor = () => (value: string) => {
  if (!value) {
    return false;
  }
  if (
    !ONE_DIGIT_PATTERN.test(value) ||
    !ONE_LOWERCASE_LETTER_PATTERN.test(value) ||
    !ONE_UPPERCASE_LETTER_PATTERN.test(value) ||
    value.length < 8
  ) {
    return { message: 'validator.password' };
  }

  return false;
};

export const email: ValidatorConstructor =
  () =>
  (value: string): ValidationResult =>
    (value && value.length && !isEmail(value) && { message: 'validator.email' }) || false;

const checkInnStep = (inn: number[], lastIndex: number): boolean => {
  const weights = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
  const sum = inn
    .slice(0, lastIndex)
    .reduce((result, digit, i) => result + digit * weights[i + weights.length - lastIndex], 0);

  return (sum % 11) % 10 === inn[lastIndex];
};

const isValidInn = (value: string): boolean => {
  const inn = value
    .trim()
    .split('')
    .map((ch) => parseInt(ch, 10));

  if (inn.length === 12) {
    return checkInnStep(inn, 10) && checkInnStep(inn, 11);
  }

  if (inn.length === 10) {
    return checkInnStep(inn, 9);
  }

  return false;
};

export const inn: ValidatorConstructor =
  () =>
  (value: string): ValidationResult =>
    !!value && !isValidInn(value) && { message: 'validator.inn' };

export const bic: ValidatorConstructor =
  () =>
  (value: string): ValidationResult =>
    !!value &&
    !/[A-Za-z]{4}[A-Za-z]{2}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?/.test(value.trim()) && {
      message: 'validator.bic',
    };

const isValidCardNumber = (value: string) => {
  const cardNumber = value.replace(/[^\d]/g, '');

  if (cardNumber.length < 13 || cardNumber.length > 19) {
    return false;
  }

  return (
    cardNumber
      .split('')
      .reverse()
      .map((symbol, i) => parseInt(symbol, 10) * (i % 2 ? 2 : 1))
      .reduce((sum, digit) => sum + (digit > 9 ? digit - 9 : digit)) %
      10 ===
    0
  );
};

export const cardNumber: ValidatorConstructor = () => (value: string) =>
  !!value &&
  !isValidCardNumber(value) && {
    message: 'validator.cardNumber',
  };

const isValidAccountNumber = (value: string, prefix?: string) => {
  const result = value.replace(/[^\d]/g, '');
  return result.length === 20 && (!prefix || result.startsWith(prefix));
};

export const accountNumber: ValidatorConstructor = (prefix?: string) => (value: string) =>
  !!value &&
  !isValidAccountNumber(value, prefix) && {
    message: 'validator.accountNumber',
  };

export const length: ValidatorConstructor =
  (length: number) =>
  (value: string): ValidationResult =>
    !!value && value.length !== length && { message: 'validator.length', details: { length } };

export const maxLength: ValidatorConstructor =
  (max: number) =>
  (value: string): ValidationResult =>
    !!value && value.length > max && { message: 'validator.maxLength', details: { max } };

export const date: ValidatorConstructor =
  (format: string, message = 'validator.date') =>
  (value: string): ValidationResult =>
    !!value && !moment(value, format, true).isValid() && { message, details: { format } };

export function validate(constraints: Constraints, values: Values) {
  const result: ValidationResults = {};

  Object.keys(constraints).forEach((name) => {
    const validators = constraints[name];
    const value = values[name];

    validators.some((validator) => {
      const error = validator(value);

      if (error) {
        result[name] = error;
        return true;
      }

      return false;
    });
  });

  return result;
}

export type ValidModel<Model> = { [P in keyof Model]?: string };
